/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import Model from 'can/model/model';
import Map from 'scripts/models/base/map';
import List from 'scripts/models/base/list';
import compare from 'vendors/compare-property/compare-property';

function getSortingFunc(sortBy, sortAsc) {
    const sortProperty = sortBy;
    const ordering = sortAsc ? 1 : -1;
    const properties = {};

    switch (sortProperty) {
        case 'player.name':
            return (a, b) => {
                let aVal = `${_.get(a, 'player.firstName', '')} ${_.get(
                    a,
                    'player.lastName',
                    '',
                )}`;
                let bVal = `${_.get(b, 'player.firstName', '')} ${_.get(
                    b,
                    'player.lastName',
                    '',
                )}`;

                return compare.compare(aVal, bVal, ordering, true);
            };
        case 'holesThrough.value':
            properties['notEmptyThru'] = -1;
            if (sortAsc) {
                properties['holesThrough.dateValue'] = ordering;
                properties[sortProperty] = ordering;
            } else {
                properties[sortProperty] = ordering;
                properties['holesThrough.dateValue'] = ordering;
            }

            return compare.properties(properties);
        case 'toParToday.value':
            return List.getPropertiesSorter(
                sortAsc ? List.ascOrdering : List.descOrdering,
                [
                    {
                        property: 'toParToday.displayValue',
                        pinnedValue: '',
                        pin: List.pinBottom,
                    },
                    { property: 'toParToday.value', stopOnNotSame: true },
                    { property: 'holesThrough.dateValue' },
                ],
            );
        case 'roundScores.0.score.value':
            properties['notEmptyRd0'] = List.invertOrdering(ordering);
            properties[sortProperty] = ordering;

            return compare.properties(properties);
        case 'roundScores.1.score.value':
            properties['notEmptyRd1'] = List.invertOrdering(ordering);
            properties[sortProperty] = ordering;

            return compare.properties(properties);
        case 'roundScores.2.score.value':
            properties['notEmptyRd2'] = List.invertOrdering(ordering);
            properties[sortProperty] = ordering;

            return compare.properties(properties);
        case 'roundScores.3.score.value':
            properties['notEmptyRd3'] = List.invertOrdering(ordering);
            properties[sortProperty] = ordering;

            return compare.properties(properties);
        case 'toPar.value':
        case 'position.value':
            properties[sortProperty] = ordering;
            properties['player.teeTime'] = ordering;

            return compare.properties(properties);
        default:
            return compare.property(sortProperty, ordering);
    }
}

export default Model.extend(
    {},
    {
        define: {
            isEnabled: {
                type: 'boolean',
                value: false,
            },
            disabledMessage: {
                type: 'string',
                value: '',
            },
            fetching: {
                type: 'boolean',
                value: true,
            },
            sortBy: {
                type: 'string',
                value: 'position.value',
            },
            sortByKey: {
                type: 'string',
                value: 'pos',
            },
            sortAsc: {
                type: 'boolean',
                value: true,
            },
            favoritesIds: {
                Type: can.List,
                Value: can.List,
            },
            hasFavorite: {
                type: 'boolean',
                value: false,
            },
            amateurOnly: {
                type: 'boolean',
                value: false,
            },
            ariaChecked: {
                type: 'boolean',
                value: false,
            },
            searchValue: {
                type: 'string',
                value: '',
            },
            currentRound: {
                type: 'number',
                value: 1,
            },
            visiblePlayersCount: {
                type: 'number',
                value: 0,
            },
            cutBoundaryPlayer: {
                Type: can.Map,
                value: null,
            },
            standings: {
                Type: Map.List,
                Value: Map.List,
                set(standings) {
                    standings.attr(
                        'comparator',
                        getSortingFunc(
                            this.attr('sortBy'),
                            this.attr('sortAsc'),
                        ),
                    );
                    return standings;
                },
            },
            sponsor: {
                Type: Map,
                value: null,
            },
            legend: {
                type: 'string',
                value: '',
            },

            playoffScoring: {
                Type: can.Map,
            },

            // getters
            show18Hole: {
                get() {
                    return !!this.attr(
                        'playoffScoring.regularPlayoff.players.length',
                    );
                },
            },

            showSuddenDeath: {
                get() {
                    return !!this.attr(
                        'playoffScoring.extraPlayoff.players.length',
                    );
                },
            },

            sponsorComponent: {
                get() {
                    if (this.attr('showSuddenDeath')) {
                        return 'sudden-death';
                    } else if (this.attr('show18Hole')) {
                        return '18-hole';
                    }
                    return 'drawer';
                },
            },
        },
        // methods

        recalculateStandings() {
            can.batch.start();
            const standings = this.attr('standings');
            const amateurOnly = this.attr('amateurOnly');
            const searchValue = this.attr('searchValue').toLowerCase();
            const favoritesIds = this.attr('favoritesIds') || [];
            const sortBy = this.attr('sortBy');
            const sortAsc = this.attr('sortAsc');
            const cutLineVal = this.attr('cutLine.position');
            let cutBoundaryPlayer = null;
            let visiblePlayersCount = 0;
            this.attr('hasFavorite', false);

            standings.forEach((standing) => {
                let visible = true;
                if (visible && amateurOnly) {
                    visible = standing.player.isAmateur === amateurOnly;
                }

                if (visible && searchValue) {
                    const firstName = standing
                        .attr('player.firstName')
                        .toLowerCase();
                    const lastName = standing
                        .attr('player.lastName')
                        .toLowerCase();
                    const firstNameAndLastName = `${standing.attr(
                        'player.firstName',
                    )} ${standing.attr('player.lastName')}`.toLowerCase();
                    const lastNameAndfirstName = `${standing.attr(
                        'player.lastName',
                    )} ${standing.attr('player.firstName')}`.toLowerCase();

                    visible =
                        firstName.indexOf(searchValue) === 0 ||
                        lastName.indexOf(searchValue) === 0 ||
                        firstNameAndLastName.indexOf(searchValue) === 0 ||
                        lastNameAndfirstName.indexOf(searchValue) === 0;
                }

                if (visible) {
                    visiblePlayersCount++;
                }

                const isFavorite =
                    favoritesIds.indexOf(standing.player.identifier) !== -1;
                if (isFavorite && visible) {
                    this.attr('hasFavorite', true);
                }

                standing.attr('visible', visible);
                standing.attr('player').attr('isFavorite', isFavorite);
                standing.attr('sortByKey', this.attr('sortByKey'));

                // find last player before cutting line
                if (
                    visible &&
                    cutLineVal &&
                    sortAsc &&
                    sortBy === 'position.value'
                ) {
                    cutBoundaryPlayer =
                        standing.attr('position.value') <= cutLineVal
                            ? standing.attr('player')
                            : cutBoundaryPlayer;
                }
            });

            this.attr('cutBoundaryPlayer', cutBoundaryPlayer);
            this.attr('visiblePlayersCount', visiblePlayersCount);
            can.batch.stop();

            return standings;
        },

        updateSort() {
            this.attr(
                'standings.comparator',
                getSortingFunc(this.attr('sortBy'), this.attr('sortAsc')),
            );
        },

        sortList(sortBy, sortByKey) {
            can.batch.start();
            if (sortByKey === this.attr('sortByKey')) {
                this.attr('sortAsc', !this.attr('sortAsc'));
            } else {
                this.attr('sortAsc', true);
                this.attr('sortBy', sortBy);
                this.attr('sortByKey', sortByKey);
            }
            this.attr(
                'standings.comparator',
                getSortingFunc(sortBy, this.attr('sortAsc')),
            );
            can.batch.stop();
            this.recalculateStandings();
        },

        filterAmateurs(value) {
            this.attr('amateurOnly', value);
            this.attr('ariaChecked', value);
            this.recalculateStandings();
        },

        filterSearch(value) {
            this.attr('searchValue', value.trim());
            this.recalculateStandings();
        },
    },
);
